import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Activity } from "lucide-react";

export default function ProcessLog({ projectId, mode }) {
  const [data, setData] = useState(null);
  const [step, setStep] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/process-log`, { params: step ? { step } : {} });
      setData(data);
    } catch { /* empty until first build */ }
  }, [projectId, step]);

  // re-fetch when explanation mode changes so explanations update (never the events)
  useEffect(() => { load(); }, [load, mode]);

  const events = data?.events || [];
  // Patch 52 — GLANCEABLE current step (the most recent event) + progress; the full scrollable log and
  // its step filters move behind a keyboard-accessible "View full log" toggle. Nothing is removed.
  const current = events.length ? events[events.length - 1] : null;
  const eventRow = (e) => (
    <div key={e.id} className="k-control" style={{ marginBottom: 8 }} data-testid={`process-event-${e.step}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>
          <span className="k-muted" style={{ fontWeight: 600, marginRight: 6 }}>Event</span>{e.step}
        </span>
        <span className="k-muted" style={{ fontSize: 12 }}>
          {String(e.at).slice(11, 19)} · {e.cost_running_credits}/{e.quote_credits} cr
        </span>
      </div>
      <div className="k-muted" style={{ fontSize: 13, marginTop: 4 }}>
        <span className="k-tag-info" style={{ fontWeight: 600, marginRight: 6 }}>Explanation</span>{e.explanation}
      </div>
    </div>
  );
  return (
    <div className="k-container" data-testid="process-log" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Activity size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Live process log</h3>
        <span className="k-badge" style={{ marginLeft: "auto" }}>{data?.mode || mode}</span>
      </div>
      {current ? (
        <div className="k-control" data-testid="process-current-step">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{current.step}</span>
            <span className="k-muted" data-testid="process-current-progress" style={{ fontSize: 12 }}>
              {current.cost_running_credits}/{current.quote_credits} cr
            </span>
          </div>
          <div className="k-muted" style={{ fontSize: 13, marginTop: 4 }}>{current.explanation}</div>
        </div>
      ) : (
        <p className="k-muted" style={{ fontSize: 13 }} data-testid="process-log-empty">
          No events yet. Run a build to see real pipeline events with a running cost.
        </p>
      )}
      {events.length > 0 && (
        <details data-testid="process-log-full" style={{ marginTop: 12 }}>
          <summary className="k-link" data-testid="process-log-full-toggle" style={{ cursor: "pointer", fontSize: 13 }}>
            View full log ({events.length} events)
          </summary>
          <div style={{ marginTop: 12 }}>
            {data?.steps?.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                <button className={`k-badge ${step === "" ? "k-badge-accent" : ""}`} style={{ cursor: "pointer" }}
                  data-testid="process-filter-all" onClick={() => setStep("")}>all</button>
                {data.steps.map(s => (
                  <button key={s} className={`k-badge ${step === s ? "k-badge-accent" : ""}`} style={{ cursor: "pointer" }}
                    data-testid={`process-filter-${s}`} onClick={() => setStep(s)}>{s}</button>
                ))}
              </div>
            )}
            {events.map(eventRow)}
          </div>
        </details>
      )}
    </div>
  );
}
